<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h3 class="text-xl font-bold text-hui-foreground">Настройки Telegram</h3>
      <p class="text-sm text-hui-foreground/60 mt-1">Настройте параметры Telegram бота</p>
    </div>

    <div v-if="loading" class="text-sm text-hui-foreground/60">Загрузка настроек…</div>

    <template v-else>
      <form class="space-y-4" @submit.prevent="saveSettings">
        <div class="flex items-center justify-between rounded-xl border border-hui-divider p-3">
          <div>
            <p class="font-medium text-hui-foreground">Авторизация через Telegram</p>
            <p class="text-xs text-hui-foreground/60">Когда выключено — кнопка входа через Telegram скрыта у клиента</p>
          </div>
          <UiKitSwitch v-model="enabled" color="success" />
        </div>

        <UiKitInput
          v-model="form.name"
          label="Имя бота"
          placeholder="Введите имя Telegram бота"
        />

        <UiKitInput
          v-model="form.bot_token"
          type="password"
          label="Bot Token"
          placeholder="Введите Bot Token (не отображается из соображений безопасности)"
        />

        <p class="text-xs text-hui-foreground/60">
          Bot Token не отображается из соображений безопасности. Введите новое значение для обновления.
        </p>

        <div class="flex justify-end gap-3 pt-4 border-t border-hui-divider">
          <UiKitButton type="button" color="default" variant="flat" @click="closeModal">
            Отмена
          </UiKitButton>
          <UiKitButton type="submit" color="primary" :loading="loading">
            Сохранить настройки
          </UiKitButton>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import consola from 'consola';
import { ref, onMounted } from 'vue';
import { useKitToast } from '@shared/ui/kit';
import { APIAdminInstitution } from '~/fsd-pages/admin/api';
import { useModalStore } from '@shared/model';
import type { TelegramSettings } from '@entities/institution';

const emit = defineEmits<{
  'update:size': [size: string]
}>();

const { toast: kitToast } = useKitToast();
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    kitToast({ color: o.color === 'green' ? 'success' : o.color === 'red' ? 'danger' : o.color === 'orange' ? 'warning' : 'default', title: o.title, description: o.description, timeout: o.timeout }),
};

const modalStore = useModalStore();
const loading = ref(false);

const form = ref<TelegramSettings>({
  name: '',
  bot_token: '',
});
// Мастер-флаг включения авторизации (institution_telegram.auth_enabled)
const enabled = ref(false);

const loadSettings = async () => {
  try {
    loading.value = true;
    const response = await APIAdminInstitution.getTelegramSettings();

    // Данные приходят в объекте telegram
    const settings = response.telegram || response;

    form.value = {
      name: settings.name || '',
      bot_token: settings.bot_token || '',
    };
    enabled.value = settings.auth_enabled === true;
  } catch (error) {
    consola.error('Ошибка загрузки настроек Telegram:', error);
    toast.add({ title: 'Ошибка', description: 'Ошибка загрузки настроек', color: 'red' });
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  try {
    loading.value = true;

    await APIAdminInstitution.updateTelegramSettings(form.value);
    await APIAdminInstitution.updateTelegramAuthEnabled(enabled.value);
    toast.add({ title: 'Сохранено', description: 'Настройки Telegram успешно сохранены', color: 'green' });

    setTimeout(() => {
      closeModal();
    }, 1500);
  } catch (error) {
    consola.error('Ошибка сохранения настроек Telegram:', error);
    toast.add({ title: 'Ошибка', description: 'Ошибка сохранения настроек', color: 'red' });
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  modalStore.close();
};

onMounted(() => {
  emit('update:size', 'l');
  loadSettings();
});
</script>
